// Roher Cluster-Snapshot für Cluster-Page + hosts-summary.
//
// Aggregiert nichts -- liefert die ClusterStats[] wie das Backend sie
// liefert. Konsumenten:
//   - pages/Cluster.svelte      → komplette Liste; das Detail eines
//                                 Rechners klappt dort in der Zeile auf
//                                 (lib/cluster/HostDetail.svelte, seit
//                                 29.09.2026 statt pages/Host.svelte)
//   - lib/hosts-summary         → leitet seine Aggregate daraus ab
//
// Dünne Sicht auf den gemeinsamen Stand in rechner.svelte.ts: seit 03.11.2026
// kommen die Rechner und ihre laufenden Werte über das Homelab-Protokoll
// (host.<id>.stand, host.<id>.stats, host.lage; docs/protokoll.md › „WAN und
// Rechner über das Protokoll“), REST nur beim Start im gemeinsamen Stand. Kein
// eigener Takt; der WS-Kanal "cluster:stats" ist seit 03.11.2026 abgebaut.
// Solange Cluster- oder Host-Seite offen sind, takten die Agents dazu auf 2 s
// hoch (Präsenz-Kanal "hosts:live", siehe Seiten).
//
// SWR: ja -- der Snapshot kommt beim Start aus der Ablage von rechner.svelte.ts;
// das erste Paint ist sofort da, frische Daten kommen Sekundenbruchteile später.
//
// Subscriber-Hook: Konsumenten können `subscribeClusterStats(cb)` rufen, um auf
// jede Änderung dessen zu reagieren, was sie auswerten (z.B. hosts-summary, das
// aus dem rohen Snapshot sein eigenes Aggregat zieht). Gemeldet wird nur bei
// geänderter Signatur (online, sleep_ok, Updates) -- Werte wie CPU ändern sich
// mit jedem Stand und sollen nur die Seiten neu zeichnen.

import { untrack } from "svelte";

import { getClusterStatsAusRechner, refreshRechner, startRechner } from "./rechner.svelte";
import type { ClusterStats } from "./types";

let _off: (() => void) | undefined;
let _beobachter: (() => void) | undefined;
const _subscribers = new Set<(s: ClusterStats[]) => void>();
let _lastSig = "";

// signature fasst zusammen, was Subscriber (hosts-summary) auswerten:
// Online-Zustand und Updates.
function signature(s: ClusterStats[]): string {
  return s
    .map((c) => c.hosts.map((h) => `${h.id}:${h.online}:${h.sleep_ok}:${JSON.stringify(h.updates ?? null)}`).join(","))
    .join("|");
}

function melde(s: ClusterStats[]): void {
  for (const cb of _subscribers) {
    try {
      cb(s);
    } catch {
      /* ignore */
    }
  }
}

export function startClusterStats(): void {
  if (_off) return;
  _off = startRechner();
  _lastSig = "";
  // Meldet die Subscriber, sobald sich die Signatur des Snapshots ändert (der
  // erste Snapshot -- aus der Ablage oder dem Abruf -- meldet immer).
  _beobachter = $effect.root(() => {
    $effect(() => {
      const s = getClusterStatsAusRechner();
      if (!s) return;
      const sig = signature(s);
      if (sig === _lastSig) return;
      _lastSig = sig;
      untrack(() => melde(s));
    });
  });
}

export function stopClusterStats(): void {
  _beobachter?.();
  _beobachter = undefined;
  _off?.();
  _off = undefined;
  _lastSig = "";
}

export function getClusterStats(): ClusterStats[] | undefined {
  return getClusterStatsAusRechner();
}

export function refreshClusterStats(): Promise<void> {
  return refreshRechner(["hosts"]);
}

export function subscribeClusterStats(cb: (s: ClusterStats[]) => void): () => void {
  _subscribers.add(cb);
  const current = getClusterStatsAusRechner();
  if (current) {
    try {
      cb(current);
    } catch {
      /* ignore */
    }
  }
  return () => {
    _subscribers.delete(cb);
  };
}
